import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/api';
import { rememberTribunalCase } from '../../lib/caseLinks';
import type { TribunalCase, TribunalFilingStatus } from '../../lib/types';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, PageHeader, Pagination, Spinner, StatusBadge } from '../../ui/components';

const STATUSES: TribunalFilingStatus[] = ['PENDING_TRANSMISSION', 'TRANSMITTED'];

export function TribunalCases() {
  const [status, setStatus] = useState('');
  const query = new URLSearchParams();
  if (status) query.set('status', status);
  const path = `/api/v1/m16/tribunal-cases${query.toString() ? `?${query.toString()}` : ''}`;
  const listQ = useCursorList<TribunalCase>(path);

  useEffect(() => {
    for (const t of listQ.items) rememberTribunalCase(t.grievanceId, t.id);
  }, [listQ.items]);

  return (
    <div>
      <PageHeader title="Tribunal Cases" subtitle="Grievances certified to the employment tribunal after conciliation failed (M37 e-filing seam)." />
      <div className="mb-4">
        <Field label="Filing status" className="!mb-0 max-w-xs">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No tribunal cases match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Cert. ref</th>
                <th className="table-head">Grievance</th>
                <th className="table-head">M37 case no.</th>
                <th className="table-head">Transmitted</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/tribunal/${t.id}`}>
                      {t.certRefNo}
                    </Link>
                  </td>
                  <td className="table-cell font-mono text-xs text-slate-500">{t.grievance?.refNo ?? t.grievanceId}</td>
                  <td className="table-cell font-mono text-xs">{t.m37CaseNumber ?? '—'}</td>
                  <td className="table-cell text-xs">{t.transmittedAt ? formatDate(t.transmittedAt) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={t.filingStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}
